import { format, parseISO } from 'date-fns';
import { Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { CourierPickup } from '@/api/types/pickups';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { PickupStatusBadge } from './PickupStatusBadge';

type Props = {
  rows: CourierPickup[];
  isLoading: boolean;
  onOpen: (pickup: CourierPickup) => void;
  emptyState: ReactNode;
};

export function CourierPickupsTable({ rows, isLoading, onOpen, emptyState }: Props) {
  const { t } = useTranslation('pickup-orders');

  const formatDate = (iso: string) => format(parseISO(iso), 'dd.MM.yyyy');

  return (
    <div className="overflow-x-auto rounded-xl border border-[#e5e5e5]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('table.pickupPoint')}</TableHead>
            <TableHead>{t('table.contactPerson')}</TableHead>
            <TableHead>{t('table.dateCreated')}</TableHead>
            <TableHead>{t('table.dateCollected')}</TableHead>
            <TableHead>{t('table.status')}</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                {t('table.loading')}
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>{emptyState}</TableCell>
            </TableRow>
          ) : (
            rows.map((pickup) => (
              <TableRow key={pickup.id}>
                <TableCell className="font-medium text-black">
                  {pickup.pickup_location?.name ?? '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {pickup.contact_person?.full_name ?? '—'}
                </TableCell>
                <TableCell>{formatDate(pickup.created_at)}</TableCell>
                <TableCell>{formatDate(pickup.pickup_time_from)}</TableCell>
                <TableCell>
                  <PickupStatusBadge status={pickup.status} group={pickup.status_group} />
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => onOpen(pickup)}
                    aria-label={t('table.viewDetails')}
                    className="grid size-8 place-items-center rounded-md border border-[#e5e5e5] bg-white hover:bg-accent"
                  >
                    <Pencil className="size-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
